// src/components/PilotConsentCard.jsx
// Consentement daté du collectionneur à l'utilisation de ses données de plateforme pour un pilote.
// Table pilot_consents ; écritures par les fonctions pilot_consent_grant / pilot_consent_revoke.
// N'apparaît que si la collection contient des œuvres venant d'une plateforme (external_source).
// Un retrait de consentement ne supprime rien : la suppression passe par le journal des imports ou par l'équipe.
import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { useFT } from "../locales/foundationCopy";

export const CONSENT_TEXT_VERSION = "pilote-v1-2026-10";
const DAYS = [[30, "30 jours"], [90, "90 jours"], [180, "6 mois"]];
const BORDER = "#e5e3df";

const box = { border: `1px solid ${BORDER}`, borderRadius: 4, background: "#fff", padding: 10, marginBottom: 6, fontSize: 12, fontFamily: "system-ui, sans-serif" };
const btn = { padding: "6px 10px", border: `1px solid ${BORDER}`, borderRadius: 4, background: "#fff", fontSize: 12, cursor: "pointer", fontFamily: "system-ui, sans-serif" };

const fmt = (d, loc = "fr-FR") => new Date(d).toLocaleDateString(loc);

export default function PilotConsentCard({ collectionId, canManage }) {
  const { T, locale } = useFT();
  const [platforms, setPlatforms] = useState([]);
  const [consent, setConsent] = useState(null); // dernier consentement (actif ou non)
  const [open, setOpen] = useState(false);
  const [platform, setPlatform] = useState("");
  const [days, setDays] = useState(90);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    if (!collectionId) return;
    const { data: arts } = await supabase
      .from("artworks_secure").select("external_source").eq("collection_id", collectionId).not("external_source", "is", null);
    const list = [...new Set((arts || []).map((a) => a.external_source).filter(Boolean))];
    setPlatforms(list);
    setPlatform((p) => p || list[0] || "");
    const { data: rows } = await supabase
      .from("pilot_consents").select("id,platform,granted_at,expires_at,revoked_at")
      .eq("collection_id", collectionId).order("granted_at", { ascending: false }).limit(1);
    setConsent(rows?.[0] || null);
  }, [collectionId]);

  useEffect(() => { load().catch(() => {}); }, [load]);

  if (!canManage || platforms.length === 0) return null;

  const active = consent && !consent.revoked_at && new Date(consent.expires_at) > new Date();

  const grant = async () => {
    setBusy(true); setMsg(null);
    const { error } = await supabase.rpc("pilot_consent_grant", {
      p_collection_id: collectionId, p_platform: platform, p_text_version: CONSENT_TEXT_VERSION, p_days: days,
    });
    setBusy(false);
    if (error) {
      setMsg(String(error.message || "").includes("ALREADY_ACTIVE") ? T("Un consentement est déjà actif.") : T("Enregistrement impossible pour le moment."));
      return;
    }
    setOpen(false); setAgree(false);
    await load();
  };

  const revoke = async () => {
    if (!window.confirm(T("Retirer ce consentement ? Les œuvres déjà importées ne sont pas supprimées automatiquement : utilisez le journal des imports pour annuler un import."))) return;
    setBusy(true); setMsg(null);
    const { error } = await supabase.rpc("pilot_consent_revoke", { p_consent_id: consent.id });
    setBusy(false);
    if (error) { setMsg(T("Retrait impossible pour le moment.")); return; }
    await load();
  };

  return (
    <div style={box}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{T("Consentement pilote")}</div>
      {active ? (
        <>
          <div>{T("Donné le {d} pour « {p} », jusqu'au {e}.", { d: fmt(consent.granted_at, locale), p: consent.platform, e: fmt(consent.expires_at, locale) })}</div>
          <button type="button" style={{ ...btn, marginTop: 6 }} disabled={busy} onClick={revoke}>{T("Retirer mon consentement")}</button>
        </>
      ) : (
        <>
          <div style={{ color: "#6f695f" }}>
            {consent
              ? (consent.revoked_at ? T("Consentement retiré le {d}.", { d: fmt(consent.revoked_at, locale) }) : T("Consentement expiré le {d}.", { d: fmt(consent.expires_at, locale) }))
              : T("Aucun consentement enregistré pour les œuvres venant d'une plateforme.")}
          </div>
          {!open ? (
            <button type="button" style={{ ...btn, marginTop: 6 }} onClick={() => setOpen(true)}>{T("Enregistrer mon consentement")}</button>
          ) : (
            <div style={{ marginTop: 8 }}>
              <label style={{ display: "block", marginBottom: 6 }}>
                {T("Plateforme")}
                <select value={platform} onChange={(e) => setPlatform(e.target.value)} style={{ display: "block", width: "100%", padding: 6, marginTop: 2 }}>
                  {platforms.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </label>
              <label style={{ display: "block", marginBottom: 6 }}>
                {T("Durée")}
                <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ display: "block", width: "100%", padding: 6, marginTop: 2 }}>
                  {DAYS.map(([v, l]) => <option key={v} value={v}>{T(l)}</option>)}
                </select>
              </label>
              <p style={{ margin: "6px 0", lineHeight: 1.45 }}>
                {T("J'autorise ARTICHAUT à utiliser les données de cette collection venant de la plateforme choisie, pour un pilote de documentation et de transmission, pendant la durée indiquée. Ces données ne sont pas transmises à la plateforme sans mon accord. Aucun mot de passe ni secret d'accès n'est saisi. Je peux retirer mon consentement et demander la suppression des œuvres importées à tout moment.")}
              </p>
              <label style={{ display: "flex", gap: 6, alignItems: "flex-start", marginBottom: 6 }}>
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} style={{ marginTop: 2 }} />
                <span>{T("J'ai lu et j'accepte.")}</span>
              </label>
              <button type="button" style={{ ...btn, background: "#1a1a1a", color: "#fff" }} disabled={busy || !agree || !platform} onClick={grant}>{T("Enregistrer")}</button>
              <button type="button" style={{ ...btn, marginLeft: 6 }} onClick={() => setOpen(false)}>{T("Annuler")}</button>
            </div>
          )}
        </>
      )}
      {msg && <div style={{ color: "#b4572f", marginTop: 6 }}>{msg}</div>}
    </div>
  );
}
